/**
 * "What would this alert rule do right now?" — pure derivation shared by the
 * AlertRuleDialog's `currently N` readout and its would-fire indicator (S24
 * review: a threshold was previously judged blind, with no sight of the value
 * it is compared against).
 *
 * Resolves the value EXACTLY like `StatTileView` does (the tile's configured
 * `valueColumn`, else the first numeric candidate) so the dialog can never
 * disagree with the number rendered on the tile.
 */
import type { QueryResultSet } from '../../../../types/queryExecution';
import { analyzeColumns } from '../../charts/utils/columnTypeDetection';
import { matchesAlertRule } from '../services/alertEvaluator';
import type { StatTile, TileAlertRule } from '../types/dashboardTypes';
import { resolveStatTileRefs, resolveStatValue } from './statFormat';

export type AlertValuePreview = {
  /** Raw scalar of the tile's last successful run, when resolvable. */
  value?: number;
  /** Formatted per the tile's `StatFormat` — what the tile shows. */
  text?: string;
  /** True when the CURRENT value already matches the rule being edited. */
  wouldFire: boolean;
};

export function alertValuePreview(
  tile: Pick<StatTile, 'valueColumn' | 'format'>,
  selected: QueryResultSet | undefined,
  rule: Pick<TileAlertRule, 'operator' | 'threshold'>,
): AlertValuePreview {
  // Same resolution seam `StatTileView`/the HTML snapshot use — a NAME-based
  // (or whitespace-rekeyed) `valueColumn` reads the right column here too.
  const columnInfos = analyzeColumns(selected?.columns ?? [], selected?.rows ?? []);
  const { valueColumn } = resolveStatTileRefs(tile, columnInfos);
  const outcome = valueColumn
    ? resolveStatValue(selected?.rows[0], valueColumn, tile.format)
    : undefined;
  if (!outcome?.ok) return { wouldFire: false };
  return { value: outcome.raw, text: outcome.text, wouldFire: matchesAlertRule(outcome.raw, rule) };
}
